import { useApolloClient } from '@apollo/client';
import { Stack, Typography } from '@mui/material';
import * as React from 'react';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Accordion } from '@/components/Accordion/Accordion';
import { TypographyClamped } from '@/components/typography/TypographyClamped';
import { useIsElementHovered } from '@/hooks/useIsElementHovered';
import { useModalState } from '@/hooks/useModalState';
import { DispositiveProvisionSuggestionsProvider } from '@/modules/aiDP/context/DispositiveProvisionSuggestions.provider';
import { AIOnboardingModal } from '@/modules/aiOnboarding/AIOnboardingModal/AIOnboardingModal';
import {
  AIOnboardingModalVariant,
  OpenAIOnboardingModalProps,
} from '@/modules/aiOnboarding/AIOnboardingModal/AIOnboardingModal.types';
import { AISuggestionReviewCard } from '@/modules/aiSuggestionReviewCard/AISuggestionReviewCard';
import { AIOnboardingSuggestionDrawer } from '@/modules/aiSuggestions/components/AIOnboardingSuggestionDrawer';
import { LocateSuggestionButton } from '@/modules/aiSuggestions/components/LocateSuggestionButton';
import { EntitySuggestionsProvider } from '@/modules/aiSuggestions/context/EntitySuggestions.provider';
import { useAssetProviderDataLinkingState } from '@/modules/assetProviderIntegrations/shared/hooks/useEnabledAssetsIntegrations';
import { useCanEditHouseholdData } from '@/modules/authentication/hooks/useCanEditHouseholdData';
import { useCanManageIntegrationData } from '@/modules/authentication/hooks/useCanManageIntegrationData';
import { EntityActionsMenu } from '@/modules/entities/components/EntityActionsMenu/EntityActionsMenu';
import { EntityDetailsAwareRoute } from '@/modules/entities/contexts/entityDetails/EntityDetailsAwareRoute';
import { EditEntitySection } from '@/modules/entities/EditEntitySplitScreen/EditEntitySplitScreen.types';
import { EditEntitySplitScreenModal } from '@/modules/entities/EditEntitySplitScreen/EditEntitySplitScreenModal';
import { EntityType } from '@/modules/entities/types/EntityType';
import { getEntityTypeFromEntityKind } from '@/modules/entities/utils/getEntityTypeFromEntityKind';
import { AddHoldingsFormModal } from '@/modules/estate/components/AddHoldingsFormModal/AddHoldingsFormModal';
import { HoldingsGroupActions } from '@/modules/estate/components/HoldingsGroupActions/HoldingsGroupActions';
import { IntegrationConnectionWarningModal } from '@/modules/estate/components/IntegrationConnectionWarningModal';
import { useEstateHoldingsContext } from '@/modules/estate/contexts/estateHoldings.context';
import { useEstateOnboardingUnacknowledgedSuggestionsContext } from '@/modules/estate/estateAIOnboarding/context/estateOnboardingUnacknowledgedSuggestions.context';
import {
  getUnacknowledgedHoldingsSuggestionsWhere,
  isEstateHoldingSuggestion,
} from '@/modules/estate/estateAIOnboarding/estateOnboarding.utils';
import { LinkIntegrationEntityFormModal } from '@/modules/estate/EstateDirectHoldings/DirectHoldingsGroupContainer/LinkIntegrationEntityFormModal/LinkIntegrationEntityFormModal';
import { useFormattedBusinessSubtitle } from '@/modules/estate/EstateDirectHoldings/EstateDirectHoldings.utils';
import {
  EstatePage_EntityFragment,
  EstatePageDataDocument,
} from '@/modules/estate/graphql/EstatePageData.generated';
import { useEstateSuggestionGenerationState } from '@/modules/estate/hooks/useEstateSuggestionGenerationState';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import { ROUTE_KEYS } from '@/navigation/constants';
import { getCompletePathFromRouteKey } from '@/navigation/navigationUtils';
import { WorkflowTrackerKind } from '@/types/schema';

import { EntityAccordionContent } from '../components/EntityAccordionContent/EntityAccordionContent';

interface BusinessSectionProps {
  business: EstatePage_EntityFragment;
  onEditEntitySection?: (entityId: string, section: EditEntitySection) => void;
}

export function EstateBusinessEntityCard({
  business,
  onEditEntitySection,
}: BusinessSectionProps) {
  const navigate = useNavigate();
  const apolloClient = useApolloClient();
  const canEdit = useCanEditHouseholdData();
  const canManageIntegrationData = useCanManageIntegrationData();
  const {
    householdId,
    primaryClients,
    displayName: householdDisplayName,
  } = useHouseholdDetailsContext();
  const refetchEstatePageData = useCallback(() => {
    void apolloClient.refetchQueries({ include: [EstatePageDataDocument] });
  }, [apolloClient]);
  const [isAddHoldingModalOpen, setIsAddHoldingModalOpen] = useState(false);
  const [isLinkIntegrationModalOpen, setIsLinkIntegrationModalOpen] =
    useState(false);
  const [
    isIntegrationConnectionWarningModalOpen,
    setIsIntegrationConnectionWarningModalOpen,
  ] = useState(false);

  const { integrationsAvailableForDataLinking, canConnectToIntegrations } =
    useAssetProviderDataLinkingState();

  const [
    { isModalOpen: isEditEntityModalOpen, data: editEntityModalData },
    { openModal: openEditEntityModal, closeModal: closeEditEntityModal },
  ] = useModalState<{
    entityType: EntityType;
    initialSection: EditEntitySection;
  }>();

  const [isHovered, hoverProps] = useIsElementHovered();
  const { additionalReferenceSuggestions, refetch: refetchSuggestions } =
    useEstateOnboardingUnacknowledgedSuggestionsContext();
  const { unacknowledgedSuggestionsV2 } = useEstateHoldingsContext();
  const isGeneratingEntityDetailsSuggestions =
    useEstateSuggestionGenerationState({
      entityId: business.id,
      workflowTrackerKind:
        WorkflowTrackerKind.AiSuggestionsV2EntityDetailsGeneration,
      onCompleted: refetchSuggestions,
    });
  const isGeneratingDPSuggestions = useEstateSuggestionGenerationState({
    entityId: business.id,
    workflowTrackerKind: WorkflowTrackerKind.AiSuggestionsV2DpGeneration,
    onCompleted: refetchSuggestions,
  });
  const isGeneratingSuggestions =
    isGeneratingEntityDetailsSuggestions || isGeneratingDPSuggestions;

  const handleLinkIntegrationClick = useCallback(() => {
    if (!householdId) return;

    if (integrationsAvailableForDataLinking.length === 0) {
      setIsIntegrationConnectionWarningModalOpen(true);
      return;
    }

    setIsLinkIntegrationModalOpen(true);
  }, [householdId, integrationsAvailableForDataLinking.length]);

  const handleNavigateToEntitySection = useCallback(
    (section: EditEntitySection) => {
      if (onEditEntitySection) {
        onEditEntitySection(business.id, section);
        return;
      }

      const entityType = getEntityTypeFromEntityKind(business.kind);
      openEditEntityModal({ entityType, initialSection: section });
    },
    [business.id, business.kind, onEditEntitySection, openEditEntityModal]
  );

  const entitySuggestionsV2 = unacknowledgedSuggestionsV2[business.id];
  const businessHoldingsSuggestions = useMemo(
    () => entitySuggestionsV2?.filter(isEstateHoldingSuggestion) ?? [],
    [entitySuggestionsV2]
  );

  // Modal state for AI Onboarding
  const [aiOnboardingState, aiOnboardingModal] =
    useModalState<
      Pick<OpenAIOnboardingModalProps, 'initialSelectedSuggestion'>
    >();

  // Modal state for Suggestion Document Drawer
  const [suggestionDrawerState, suggestionDrawerModal] = useModalState<{
    suggestionId: string;
  }>();

  const handleOpenAIOnboardingModal = useCallback(
    ({
      initialSelectedSuggestion,
    }: Pick<OpenAIOnboardingModalProps, 'initialSelectedSuggestion'>) => {
      aiOnboardingModal.openModal({ initialSelectedSuggestion });
    },
    [aiOnboardingModal]
  );

  const handleLocateSuggestion = useCallback(() => {
    const suggestion = business.onboardingEntitySuggestion?.[0];
    if (suggestion) {
      suggestionDrawerModal.openModal({ suggestionId: suggestion.id });
    }
  }, [business.onboardingEntitySuggestion, suggestionDrawerModal]);

  const handleTitleClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (!householdId) return;
    const entityDetailsUrl = getCompletePathFromRouteKey(
      ROUTE_KEYS.HOUSEHOLD_ENTITY_DETAILS,
      {
        entityId: business.id,
        householdId,
      }
    );
    navigate(entityDetailsUrl);
  };

  const subtitle = useFormattedBusinessSubtitle(
    business,
    primaryClients,
    householdDisplayName
  );

  return (
    <>
      <Accordion
        variant="filled"
        responsiveRightAdornment
        title={
          <Stack direction="row" gap={1} alignItems="center">
            <Typography
              variant="title-xs"
              onClick={handleTitleClick}
              sx={{
                textDecoration: isHovered ? 'underline' : 'none',
                cursor: isHovered ? 'pointer' : 'default',
                transition: 'text-decoration 0.2s ease, cursor 0.2s ease',
              }}
            >
              {business.subtype.displayName}
            </Typography>
            {isHovered && business.onboardingEntitySuggestion?.[0] && (
              <LocateSuggestionButton
                suggestionID={business.onboardingEntitySuggestion[0].id}
                onLocateSuggestionClick={handleLocateSuggestion}
                dataTestId={`business-locate-suggestion-${business.id}`}
                iconProps={{ size: 12 }}
                sx={{
                  width: '16px',
                  minWidth: '16px',
                  height: '16px',
                  p: '2px',
                }}
              />
            )}
          </Stack>
        }
        subtitle={
          <TypographyClamped
            variant="body-xs"
            lines={1}
            tooltipProps={{
              title: subtitle,
            }}
          >
            {subtitle}
          </TypographyClamped>
        }
        isDefaultExpanded={false}
        {...hoverProps}
        rightAdornment={
          <Stack
            direction="row"
            spacing={1}
            onClick={(event) => {
              event.stopPropagation();
            }}
          >
            <HoldingsGroupActions
              entity={business}
              onAddHoldingClick={() => setIsAddHoldingModalOpen(true)}
              onLinkIntegrationClick={handleLinkIntegrationClick}
              isHovered={isHovered}
              isGeneratingSuggestions={isGeneratingSuggestions}
              holdingsSuggestions={businessHoldingsSuggestions}
              entitySuggestionsV2={entitySuggestionsV2}
              onHoldingsSuggestionClick={() => {
                handleOpenAIOnboardingModal({
                  initialSelectedSuggestion: undefined,
                });
              }}
              onEditEntitySectionSuggestionClick={handleNavigateToEntitySection}
            />
            {(canEdit || canManageIntegrationData) && householdId && (
              <EntityActionsMenu
                entity={business}
                householdId={householdId}
                contextMenuButtonProps={{ size: 'xs', variant: 'transparent' }}
                onAfterDelete={refetchEstatePageData}
                onAfterConvert={refetchEstatePageData}
                onAfterDuplicate={refetchEstatePageData}
                onEdit={handleNavigateToEntitySection}
              />
            )}
          </Stack>
        }
        withDivider={false}
      >
        <Stack spacing={1}>
          {canEdit && (
            <AISuggestionReviewCard
              variant={AIOnboardingModalVariant.HOLDINGS}
              suggestionDisplayText={{
                singular: 'holding',
                plural: 'holdings',
              }}
              suggestions={businessHoldingsSuggestions}
              additionalReferenceSuggestions={additionalReferenceSuggestions}
              onOpenAIOnboardingModal={handleOpenAIOnboardingModal}
              showProcessingBanner={false}
              onSuggestionCreated={refetchSuggestions}
            />
          )}
          <EntityAccordionContent entity={business} />
        </Stack>
      </Accordion>
      <AddHoldingsFormModal
        holdingsGroup={business}
        open={isAddHoldingModalOpen}
        onClose={() => setIsAddHoldingModalOpen(false)}
      />
      <LinkIntegrationEntityFormModal
        entityId={business.id}
        open={isLinkIntegrationModalOpen}
        onClose={() => setIsLinkIntegrationModalOpen(false)}
      />
      <IntegrationConnectionWarningModal
        isOpen={isIntegrationConnectionWarningModalOpen}
        onClose={() => setIsIntegrationConnectionWarningModalOpen(false)}
        canConnectToIntegrations={canConnectToIntegrations}
      />
      {aiOnboardingState.isModalOpen && (
        <AIOnboardingModal
          isOpen={aiOnboardingState.isModalOpen}
          onClose={() => aiOnboardingModal.closeModal()}
          variant={AIOnboardingModalVariant.HOLDINGS}
          householdId={householdId!}
          initialSelectedSuggestion={
            aiOnboardingState.data?.initialSelectedSuggestion
          }
          suggestionsWhere={getUnacknowledgedHoldingsSuggestionsWhere(
            business.id
          )}
          additionalReferenceSuggestions={additionalReferenceSuggestions}
          skipNavigationOnComplete={true}
        />
      )}
      {suggestionDrawerState.isModalOpen && suggestionDrawerState.data && (
        <AIOnboardingSuggestionDrawer
          isOpen={suggestionDrawerState.isModalOpen}
          onClose={() => suggestionDrawerModal.closeModal()}
          suggestionId={suggestionDrawerState.data.suggestionId}
        />
      )}
      {isEditEntityModalOpen && editEntityModalData && (
        <EntityDetailsAwareRoute entityId={business.id}>
          <EntitySuggestionsProvider>
            <DispositiveProvisionSuggestionsProvider targetId={business.id}>
              <EditEntitySplitScreenModal
                isOpen={isEditEntityModalOpen}
                entityType={editEntityModalData.entityType}
                initialSection={editEntityModalData.initialSection}
                onClose={closeEditEntityModal}
              />
            </DispositiveProvisionSuggestionsProvider>
          </EntitySuggestionsProvider>
        </EntityDetailsAwareRoute>
      )}
    </>
  );
}
